import React, { useCallback } from 'react';
import { createHubModalEntryPoint } from '@jira/servicedesk__customer-service-hubs/src/createHubModalEntryPoint.entrypoint.tsx';
import AddIcon from '@atlaskit/icon/core/add';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { generatePath, useRouterActions } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { csmExperienceDetailsRoute } from '@jira/router-routes__help-center-settings-routes/src/csmExperienceDetailsRoute.tsx';
import { helpCenterSettingsDetailsRoute } from '@jira/router-routes__help-center-settings-routes/src/helpCenterSettingsDetailsRoute.tsx';

export const CreateExperienceButton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();

	const onClick = useCallback(
		(_e: React.MouseEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'customerExperiencesAddCustomerExperience', {
				entry: 'Nav4Sidebar',
			});
		},
		[],
	);

	const handleCreateHub = useCallback(
		(data?: { customerHubId: string; projectKey: string }) => {
			if (data) {
				const path = generatePath(
					fg('csm_appification_standalone_navigation')
						? csmExperienceDetailsRoute.path
						: helpCenterSettingsDetailsRoute.path,
					{
						helpCenterId: data.customerHubId,
						projectKey: data.projectKey,
					},
				);
				push(path);
			}
		},
		[push],
	);

	return (
		<ModalTrigger
			entryPoint={createHubModalEntryPoint}
			entryPointProps={{ onCreateHub: handleCreateHub }}
			modalProps={{ width: 'x-large' }}
		>
			{({ ref }) => (
				<Tooltip content={formatMessage(createExperienceMessage)}>
					<span ref={ref} role="presentation">
						<CommonNavMenuButton
							icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
							label={formatMessage(createExperienceMessage)}
							onClick={onClick}
						/>
					</span>
				</Tooltip>
			)}
		</ModalTrigger>
	);
};

const createExperienceMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-customer-service.ui.create-experience',
	defaultMessage: 'Create experience',
	description:
		'Used as the tooltip text for an icon button that when clicked opens the create customer experience modal',
});
